import {useCallback, useEffect, useRef, useState} from 'react';
import baseUrl from '../api/baseUrl';
import {useCreateAuthenticatedFetch} from '../api/useCreateAuthenticatedFetch';
import type {RealtimeVoice} from './realtimeVoices';

export const useVoicePreview = () => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const [previewingVoice, setPreviewingVoice] =
        useState<RealtimeVoice | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const previewAudioContextRef = useRef<AudioContext | null>(null);
    const previewAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
    const previewRequestIdRef = useRef(0);

    const stopVoicePreview = useCallback(() => {
        previewRequestIdRef.current += 1;

        if (previewAudioSourceRef.current) {
            previewAudioSourceRef.current.onended = null;
            previewAudioSourceRef.current.stop();
            previewAudioSourceRef.current.disconnect();
            previewAudioSourceRef.current = null;
        }

        setPreviewingVoice(null);
    }, []);

    const clearPreviewError = useCallback(() => {
        setPreviewError(null);
    }, []);

    const previewVoice = useCallback(
        async (voice: RealtimeVoice) => {
            stopVoicePreview();
            const requestId = previewRequestIdRef.current;
            setPreviewingVoice(voice);
            setPreviewError(null);

            try {
                let audioContext = previewAudioContextRef.current;
                if (!audioContext || audioContext.state === 'closed') {
                    audioContext = new AudioContext();
                    previewAudioContextRef.current = audioContext;
                }

                // Resume while the click is still the active user gesture. Waiting for
                // the authenticated request first can cause browsers to block playback.
                await audioContext.resume();

                const response = await authenticatedFetch(
                    `${baseUrl}/api/voice/preview`,
                    {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({voice})
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Unable to create preview (${response.status}).`
                    );
                }

                const audioBytes = await response.arrayBuffer();
                if (audioBytes.byteLength === 0) {
                    throw new Error('The preview audio was empty.');
                }

                const audioBuffer = await audioContext.decodeAudioData(
                    audioBytes.slice(0)
                );
                if (previewRequestIdRef.current !== requestId) return;

                const source = audioContext.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(audioContext.destination);
                previewAudioSourceRef.current = source;
                source.onended = () => {
                    if (previewAudioSourceRef.current === source) {
                        source.disconnect();
                        previewAudioSourceRef.current = null;
                        setPreviewingVoice(current =>
                            current === voice ? null : current
                        );
                    }
                };
                source.start();
            } catch (error) {
                if (previewRequestIdRef.current !== requestId) return;

                stopVoicePreview();
                setPreviewError(
                    error instanceof Error
                        ? error.message
                        : 'Unable to play the preview.'
                );
            }
        },
        [authenticatedFetch, stopVoicePreview]
    );

    useEffect(
        () => () => {
            stopVoicePreview();
            void previewAudioContextRef.current?.close();
            previewAudioContextRef.current = null;
        },
        [stopVoicePreview]
    );

    return {
        clearPreviewError,
        previewError,
        previewingVoice,
        previewVoice,
        stopVoicePreview
    };
};
